import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Cardinal } from '../types';

interface CardinalDetailProps {
  cardinaux: Cardinal[];
}

const CardinalDetail: React.FC<CardinalDetailProps> = ({ cardinaux }) => {
  const { slug } = useParams<{ slug: string }>();
  const [cardinal, setCardinal] = useState<Cardinal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !cardinaux.length) {
      setLoading(false);
      return;
    }

    // Convertir le slug en nom de cardinal (remplacer les tirets par des espaces)
    const cardinalName = slug.replace(/-/g, ' ');
    
    console.log('Recherche du cardinal avec slug:', slug);
    console.log('Nom du cardinal recherché:', cardinalName);
    
    // Fonction pour normaliser les noms (enlever les accents et mettre en minuscules)
    const normalizeText = (text: string): string => {
      return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };
    
    // Essayer de trouver le cardinal par correspondance exacte d'abord
    const normalizedSearchName = normalizeText(cardinalName);
    let foundCardinal = cardinaux.find(c => normalizeText(c.name) === normalizedSearchName);
    
    // Si pas de correspondance exacte, essayer par mots-clés
    if (!foundCardinal) {
      // Extraire les mots significatifs (plus de 3 lettres, pas des mots communs)
      const significantWords = cardinalName.toLowerCase().split(' ')
        .filter(word => word.length > 3 && !['jose', 'jean', 'marc', 'luis', 'cardinal', 'von', 'van', 'del', 'dos', 'das', 'les', 'san'].includes(word));
      
      if (significantWords.length > 0) {
        // Rechercher les cardinaux qui contiennent ces mots significatifs
        const candidates = cardinaux.filter(c => {
          const normalizedName = normalizeText(c.name);
          return significantWords.some(word => normalizedName.includes(normalizeText(word)));
        });
        
        // Si plusieurs candidats, prendre celui avec le plus de correspondances
        if (candidates.length > 0) {
          foundCardinal = candidates.reduce((best, current) => {
            const bestMatches = significantWords.filter(word => normalizeText(best.name).includes(normalizeText(word))).length;
            const currentMatches = significantWords.filter(word => normalizeText(current.name).includes(normalizeText(word))).length;
            return currentMatches > bestMatches ? current : best;
          }, candidates[0]);
        }
      }
    }
    
    // Si toujours pas trouvé, essayer par inclusion partielle
    if (!foundCardinal) {
      foundCardinal = cardinaux.find(c => {
        const normalizedCardinalName = normalizeText(c.name);
        return normalizedCardinalName.includes(normalizedSearchName) || normalizedSearchName.includes(normalizedCardinalName);
      });
    }
    
    // Si un cardinal est trouvé, le définir
    if (foundCardinal) {
      console.log('Cardinal trouvé:', foundCardinal.name);
      setCardinal(foundCardinal);
    } else {
      console.log('Aucun cardinal trouvé pour:', cardinalName);
      // Créer un cardinal générique avec le nom du slug pour éviter une page vide
      const genericCardinal: Cardinal = {
        name: cardinalName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        imageUrl: '',
        isPapabili: false
      };
      setCardinal(genericCardinal);
    }
    
    setLoading(false);
  }, [slug, cardinaux]);

  // Fonction pour déterminer quel pape a créé le cardinal
  const getCreatorPope = (cardinal: Cardinal): string => {
    if (cardinal.createdByFrancis) {
      return "Pape François";
    } else {
      // Déterminer le pape en fonction de la date de création
      const creationDate = cardinal.creationDate ? new Date(cardinal.creationDate.split('/').reverse().join('-')) : null;
      
      if (creationDate) {
        // Benoît XVI: 19 avril 2005 - 28 février 2013
        const benedictStart = new Date('2005-04-19');
        const benedictEnd = new Date('2013-02-28');
        
        // Jean-Paul II: 16 octobre 1978 - 2 avril 2005
        const jpiiStart = new Date('1978-10-16');
        const jpiiEnd = new Date('2005-04-02');
        
        if (creationDate >= benedictStart && creationDate <= benedictEnd) {
          return "Benoît XVI";
        } else if (creationDate >= jpiiStart && creationDate <= jpiiEnd) {
          return "Jean-Paul II";
        }
      }
      
      return "Autre pape";
    }
  };

  // Fonction pour formater une date (JJ/MM/AAAA)
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "Non disponible";
    
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    
    return `${parts[0]} ${
      ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"][parseInt(parts[1]) - 1]
    } ${parts[2]}`;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-screen">
        <motion.div
          animate={{ rotate: 0 }}
          transition={{ duration: 0.2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-yellow-500 rounded-full border-t-transparent"
        />
      </div>
    );
  }

  // Nous savons que cardinal n'est pas null ici car nous avons une vérification de chargement au-dessus
  // Mais TypeScript ne peut pas le détecter, donc nous utilisons un opérateur de non-nullité (!) pour indiquer à TypeScript
  // que nous sommes sûrs que cardinal n'est pas null
  const cardinalData = cardinal!;

  return (
    <div className="container mx-auto px-2 sm:px-4 py-2">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gray-800 rounded-lg shadow-xl overflow-hidden"
      >
        <div className="p-3 sm:p-4">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/3 flex flex-col items-center mb-6 md:mb-0">
              {cardinalData.imageUrl && (
                <div className="mb-4">
                  <img 
                    src={cardinalData.imageUrl} 
                    alt={cardinalData.name} 
                    className="w-48 h-48 object-cover rounded-full border-4 border-yellow-500"
                    onError={(e) => {
                      console.error(`Erreur de chargement de l'image: ${cardinalData.imageUrl}`);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {cardinalData.coatOfArmsUrl && (
                <div className="mb-4">
                  <img 
                    src={cardinalData.coatOfArmsUrl} 
                    alt={`Armoiries de ${cardinalData.name}`} 
                    className="w-32 h-32 object-contain"
                    onError={(e) => {
                      console.error(`Erreur de chargement des armoiries: ${cardinalData.coatOfArmsUrl}`);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="text-center">
                <h1 className="text-2xl font-bold text-yellow-500 mb-2">{cardinalData.name}</h1>
                {cardinalData.function && (
                  <p className="text-yellow-500 mb-2">{cardinalData.function}</p>
                )}
                {cardinalData.country && (
                  <div className="flex items-center justify-center mb-2">
                    {cardinalData.flagUrl && (
                      <img 
                        src={cardinalData.flagUrl} 
                        alt={cardinalData.country} 
                        className="h-5 w-8 mr-2 object-cover"
                        onError={(e) => e.currentTarget.style.display = 'none'}
                      />
                    )}
                    <span className="text-gray-300">{cardinalData.country}</span>
                  </div>
                )}
                {cardinalData.age && (
                  <p className="text-gray-300 mb-2">
                    <span className="font-semibold">Âge:</span> {cardinalData.age} ans
                  </p>
                )}
                {cardinalData.birthDate && (
                  <p className="text-gray-300 mb-2">
                    <span className="font-semibold">Né le:</span> {formatDate(cardinalData.birthDate)}
                  </p>
                )}
                {cardinalData.creationDate && (
                  <p className="text-gray-300 mb-2">
                    <span className="font-semibold">Créé cardinal par:</span> le {getCreatorPope(cardinalData)} le {formatDate(cardinalData.creationDate)}
                  </p>
                )}
              </div>
            </div>
            
            <div className="md:w-2/3 md:pl-8">
              <h2 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-2">Biographie</h2>
              {cardinalData.biography ? (
                <div className="text-gray-300 space-y-4">
                  {cardinalData.biography.split('\n\n').map((paragraph, index) => (
                    <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 italic">Biographie non disponible</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-gray-900 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <Link 
              to="/" 
              className="text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              ← Retour à l'accueil
            </Link>
            {cardinalData.url && (
              <a 
                href={cardinalData.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                En savoir plus →
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CardinalDetail;
